import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Audio Content API service
 * 
 * Provides access to generated audio content (podcasts and audiobooks)
 * from study materials.
 */
export const AudioContentApi = {
  /**
   * Create audio content record (typically called after AI generation)
   * @param {Object} data - Audio content data
   * @param {number} data.userId - User ID
   * @param {number} [data.endeavorId] - Endeavor ID (optional)
   * @param {number} [data.documentId] - Document ID (optional)
   * @param {string} data.title - Audio title
   * @param {string} [data.description] - Audio description
   * @param {string} data.audioUrl - URL to audio file
   * @param {string} [data.format] - Format: "podcast" or "audiobook" (default: "audiobook")
   * @param {number} [data.durationSeconds] - Duration in seconds
   * @param {number} [data.generationCostUsd] - Generation cost in USD
   * @param {number[]} [data.sourceDocumentIds] - Array of source document IDs
   * @returns {Promise<Object>} Created audio content
   */
  createAudioContent: async (data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/audio-content`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Get audio content by ID
   * @param {number} id - Audio content ID
   * @returns {Promise<Object>} Audio content details
   */
  getAudioContent: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/audio-content/${id}`, {
      method: 'GET',
    });
  },

  /**
   * Get all audio content for current user (simple list)
   * @param {Object} [options] - Filter options
   * @param {string} [options.format] - Filter by format: "podcast" or "audiobook"
   * @param {number} [options.endeavorId] - Filter by endeavor ID
   * @returns {Promise<Object[]>} List of audio content
   */
  getUserAudioContent: async (options = {}) => {
    const { format, endeavorId } = options;
    const queryParams = new URLSearchParams();
    
    if (format) queryParams.set('format', format);
    if (endeavorId) queryParams.set('endeavorId', endeavorId);
    
    const queryString = queryParams.toString();
    const url = `${DATA_SERVICE_BASE_URL}/api/audio-content${queryString ? '?' + queryString : ''}`;
    
    return fetchWithAuth(url, {
      method: 'GET',
    });
  },

  /**
   * List and filter audio content with pagination
   * @param {Object} [options] - Query options
   * @param {number} [options.page=0] - Page number (0-indexed)
   * @param {number} [options.size=20] - Page size
   * @param {number} [options.endeavorId] - Filter by endeavor ID
   * @param {number} [options.documentId] - Filter by document ID
   * @param {string} [options.format] - Filter by format: "podcast" or "audiobook"
   * @param {string} [options.title] - Filter by title (partial match)
   * @param {number} [options.minDuration] - Minimum duration in seconds
   * @param {number} [options.maxDuration] - Maximum duration in seconds
   * @param {string} [options.createdAfter] - ISO datetime string
   * @param {string} [options.createdBefore] - ISO datetime string
   * @param {string} [options.sortBy="createdAt"] - Sort field
   * @param {string} [options.sortDirection="DESC"] - Sort direction: "ASC" or "DESC"
   * @returns {Promise<Object>} Paginated audio content results
   */
  listAudioContent: async (options = {}) => {
    const {
      page = 0,
      size = 20,
      endeavorId,
      documentId,
      format,
      title,
      minDuration,
      maxDuration,
      createdAfter,
      createdBefore,
      sortBy = 'createdAt',
      sortDirection = 'DESC',
    } = options;

    const queryParams = new URLSearchParams({
      page: String(page),
      size: String(size),
      sortBy,
      sortDirection,
    });

    if (endeavorId) queryParams.set('endeavorId', endeavorId);
    if (documentId) queryParams.set('documentId', documentId);
    if (format) queryParams.set('format', format);
    if (title) queryParams.set('title', title);
    if (minDuration) queryParams.set('minDuration', minDuration);
    if (maxDuration) queryParams.set('maxDuration', maxDuration);
    if (createdAfter) queryParams.set('createdAfter', createdAfter);
    if (createdBefore) queryParams.set('createdBefore', createdBefore);

    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/audio-content/list?${queryParams.toString()}`, {
      method: 'GET',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to list audio content: ${response.status}`);
    }
    
    return await response.json();
  },

  /**
   * Get audio content by endeavor
   * @param {number} endeavorId - Endeavor ID
   * @param {Object} [options] - Filter options
   * @param {string} [options.format] - Filter by format: "podcast" or "audiobook"
   * @returns {Promise<Object[]>} List of audio content for endeavor
   */
  getEndeavorAudioContent: async (endeavorId, options = {}) => {
    const { format } = options;
    const queryParams = new URLSearchParams();
    
    if (format) queryParams.set('format', format);
    
    const queryString = queryParams.toString();
    const url = `${DATA_SERVICE_BASE_URL}/api/audio-content/endeavor/${endeavorId}${queryString ? '?' + queryString : ''}`;
    
    return fetchWithAuth(url, {
      method: 'GET',
    });
  },

  /**
   * Update audio content metadata (e.g., rename title)
   * @param {number} id - Audio content ID
   * @param {Object} data - Updated data
   * @param {string} [data.title] - New title
   * @param {string} [data.description] - New description
   * @returns {Promise<Object>} Updated audio content
   */
  updateAudioContent: async (id, data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/audio-content/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete audio content
   * @param {number} id - Audio content ID
   * @returns {Promise<Object>} Delete confirmation
   */
  deleteAudioContent: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/audio-content/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get audio content statistics for current user
   * @returns {Promise<Object>} Audio content statistics
   */
  getAudioContentStats: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/audio-content/stats`, {
      method: 'GET',
    });
  },
};
